import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Users,
  Flame,
  Plug,
} from "lucide-react";
import { useTran } from "@/hooks";

const CarbonOverviewCards = ({ performance, year }) => {
  // Safety check for required props
  if (!performance || !performance.scope1 || !performance.scope2) {
    return null;
  }
  const { t } = useTran();

  const { scope1, scope2, totalSMEs, approvedSMEs } = performance;

  const yearLabel = year === "all" ? "All Years" : year;

  // Calculate overall status properly
  const calculateOverallStatus = () => {
    const avgProgress = (scope1.progress + scope2.progress) / 2;

    // If either scope is critically over or at risk
    if (scope1.status === "at-risk" || scope2.status === "at-risk") {
      return "at-risk";
    }

    // If either scope is over target
    if (scope1.status === "over-target" || scope2.status === "over-target") {
      return "over-target";
    }

    // Both are on track
    return "on-track";
  };

  const cards = [
    {
      title: `Scope 1 Emissions (${yearLabel})`,
      target: scope1.target.toFixed(2),
      actual: scope1.actual.toFixed(2),
      unit: "tCO₂e",
      variance: scope1.variance,
      progress: scope1.progress,
      status: scope1.status,
      icon: <Flame className="h-5 w-5" />,
      color: "emerald",
    },
    {
      title: `Scope 2 Emissions (${yearLabel})`,
      target: scope2.target.toFixed(2),
      actual: scope2.actual.toFixed(2),
      unit: "tCO₂e",
      variance: scope2.variance,
      progress: scope2.progress,
      status: scope2.status,
      icon: <Plug className="h-5 w-5" />,
      color: "blue",
    },
    {
      title: "SME Participation",
      value: `${approvedSMEs} / ${totalSMEs}`,
      subtitle: "Approved submissions",
      icon: <Users className="h-5 w-5" />,
      type: "info",
      color: "purple",
    },
    {
      title: "Overall Progress",
      value: `${Math.round((scope1.progress + scope2.progress) / 2)}%`,
      subtitle: "Combined progress",
      status: calculateOverallStatus(),
      icon: <Target className="h-5 w-5" />,
      type: "progress",
      color: "indigo",
    },
  ];

  const getStatusColor = (status) => {
    if (status === "on-track") return "bg-green-500 text-white font-semibold";
    if (status === "over-target")
      return "bg-orange-500 text-white font-semibold";
    return "bg-red-500 text-white font-semibold";
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card, idx) => (
        <Card key={idx} className="border-slate-200 dark:border-slate-800">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  {card.icon}
                  <p className="text-sm font-medium">{card.title}</p>
                </div>

                {card.type === "info" || card.type === "progress" ? (
                  <>
                    <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                      {card.value}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {card.subtitle}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="mt-2 flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-slate-900 dark:text-white">
                        {card.actual}
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        / {card.target} {card.unit}
                      </span>
                    </div>

                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        {card.variance >= 0 ? (
                          <TrendingUp className="h-5 w-5 text-red-500" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-green-500" />
                        )}
                        <span
                          className={`text-base font-bold ${
                            card.variance >= 0
                              ? "text-red-600 dark:text-red-400"
                              : "text-green-600 dark:text-green-400"
                          }`}
                        >
                          {card.variance >= 0 ? "+" : ""}
                          {card.variance.toFixed(0)} {card.unit}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        variance
                      </span>
                    </div>

                    <div className="mt-2">
                      <div className="relative h-2 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            card.status === "on-track"
                              ? "bg-green-500"
                              : card.status === "over-target"
                                ? "bg-orange-500"
                                : "bg-red-500"
                          }`}
                          style={{ width: `${Math.min(100, card.progress)}%` }}
                        />
                        {/* Overflow indicator */}
                        {card.progress > 100 && (
                          <div className="absolute inset-0 flex items-center justify-end pr-1">
                            <div className="h-1.5 w-1.5 rounded-full bg-orange-600 animate-pulse" />
                          </div>
                        )}
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {card.progress.toFixed(1)}% of target
                        </p>
                        {card.progress > 100 && (
                          <p className="text-xs font-medium text-orange-600 dark:text-orange-400">
                            Exceeded by {(card.progress - 100).toFixed(0)}%
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {card.status && (
                  <Badge className={`mt-2 ${getStatusColor(card.status)}`}>
                    {card.status.replace("-", " ")}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default CarbonOverviewCards;
